<template>
	<component :is="href ? 'a' : 'span'" :href="href" :class="classes" :aria-current="href ? current : false">
		<span class="StatsIcon-stat">
			<CloIcon v-if="icon" :symbol="icon" />
			<CloLoadingAnimation v-if="loading" />
			<span v-else class="StatsIcon-stat-count">{{ stat }}</span>
		</span>
		<span v-if="label" class="StatsIcon-label">{{ label }}</span>
		<span v-if="secondary" class="StatsIcon-secondary">{{ secondary }} {{ secondaryLabel }}</span>
		<span v-if="status" class="StatsIcon-status">
			<span>{{ status }}</span>
		</span>
	</component>
</template>

<script>
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';

export default {
	name: 'CloStat',
	components: {
		CloLoadingAnimation,
		CloIcon,
	},
	data() {
		return {};
	},
	props: {
		/**
		 * Icon symbol name
		 */
		icon: {
			type: String,
			default: null,
		},
		/**
		 * Main statistic (usually formatted as a string with toLocaleString())
		 */
		stat: {
			type: [String, Number],
		},
		/**
		 * Main statistic label
		 */
		label: {
			type: String,
			default: '',
		},
		/**
		 * Secondary statistic (usually formatted as a string with toLocaleString())
		 */
		secondary: {
			type: [String, Number],
		},
		/**
		 * Secondary statistic label
		 */
		secondaryLabel: {
			type: String,
			default: '',
		},
		/**
		 * Applies aria-current and changes style (only works with href prop)
		 */
		current: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show a loading spinner
		 */
		loading: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show a status message (data unavailable) or error
		 */
		status: {
			type: String,
			default: null,
		},
		/**
		 * Link to a URL
		 */
		href: {
			type: String,
			default: null,
		},
		/**
		 * Style as a tab
		 */
		tab: {
			type: Boolean,
			default: false,
		},
		/**
		 * Style with line
		 */
		line: {
			type: Boolean,
			default: false,
		},
		/**
		 * Reverse style
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Muted style for less importance
		 */
		muted: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show stat and label under icon
		 */
		vertical: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show label inline with icon and stat
		 */
		inline: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show label above icon and stat
		 */
		labelAbove: {
			type: Boolean,
			default: false,
		},
		/**
		 * Sizes: sm, md (default), lg, responsive, responsive-sm, responsive-lg
		 */
		size: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			// TODO: deprecate selected and highlight - use current instead?
			const classes = {
				StatsIcon: true,
				'StatsIcon--tab': this.tab,
				'StatsIcon--reverse': this.reverse,
				'StatsIcon--muted': this.muted,
				'StatsIcon--vertical': this.vertical,
				'StatsIcon--inline': this.inline,
				'StatsIcon--labelAbove': this.labelAbove,
				'StatsIcon--line': this.line,
			};
			if (this.size) {
				classes['StatsIcon--' + this.size] = true;
			}
			return classes;
		},
	},
};
</script>

<style lang="scss">
/* --------------------------------
	DEFAULT STRUCTURE AND STYLING
	Size and color added by variants below
-------------------------------- */

/* Main wrapper for component */
.StatsIcon {
	display: inline-flex;
	flex-direction: column;
	gap: 0.08em;
}

/* Wrapper for stat icon and count */
.StatsIcon-stat {
	display: inline-flex;
	align-items: center;
	gap: 0.16em;
	font-weight: bold;
	.Icon {
		flex-shrink: 0;
		transform: translateY(-4%);
	}
	/* Loading animation for asynchronous loading of stat */
	.LoadingAnimation {
		@include inset-xs;
		background-color: transparent;
		display: flex;
		justify-content: center;
		position: relative;
		top: auto;
		left: auto;
		bottom: auto;
		right: auto;
		.Icon {
			display: flex;
			fill: currentColor;
			opacity: 0.5;
			margin: 0;
		}
	}
	.StatsIcon-stat-count {
		flex-shrink: 0;
	}
	.StatsIcon-stat-count:empty:before {
		content: '--';
		opacity: 0.5;
	}
}

/* Label - appears underneath icon and count */
.StatsIcon-label {
	// sizes set by mixins below
}

/* Secondary - optional text for an additional stat */
.StatsIcon-secondary {
	display: none; // hide for small screens
	@include breakpoint(medium) {
		display: unset; // show for medium up screens
	}
	/* Variant to show for all screens, overriding default behavior */
	&--showSmall {
		display: unset;
	}
}

/* Status - optional status text to be added/removed with js */
.StatsIcon-status {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
}

/* ------------------------------------------------
---------------------------------------------------
	MIXINS @@@@@@@@
---------------------------------------------------
------------------------------------------------ */

/* --------------------------------
	Size mixins
-------------------------------- */

// Small variant
@mixin StatsIcon--sm {
	// @include text(5);
	font-size: 1.4rem;
	line-height: 1;
	.StatsIcon-stat {
		letter-spacing: -0.01rem;
		.Icon {
			@include icon-sm;
		}
		.LoadingAnimation {
			.Icon {
				@include icon-sm;
			}
		}
	}
	.StatsIcon-label {
		@include text(2);
		line-height: 1.1;
	}
	.StatsIcon-secondary {
		@include text(1);
		line-height: 1.1;
	}
	.StatsIcon-status {
		@include text(1);
		line-height: 1.1;
		.Button {
			@include text(1);
		}
	}
}

// Medium variant
@mixin StatsIcon--md {
	// @include text(6);
	font-size: 2rem;
	line-height: 1;
	.StatsIcon-stat {
		letter-spacing: -0.02rem;
		.Icon {
			@include icon-md;
		}
		.LoadingAnimation {
			.Icon {
				@include icon-md;
			}
		}
	}
	.StatsIcon-label {
		@include text(3);
		line-height: 1.3;
	}
	.StatsIcon-secondary {
		@include text(2);
		line-height: 1.2;
	}
	.StatsIcon-status {
		@include text(2);
		.Button {
			@include text(2);
		}
	}
}

// Large variant
@mixin StatsIcon--lg {
	// @include text(7);
	font-size: 2.4rem;
	line-height: 1;
	.StatsIcon-stat {
		letter-spacing: -0.03rem;
		.Icon {
			@include icon-lg;
		}
		.LoadingAnimation {
			.Icon {
				@include icon-lg;
			}
		}
	}
	.StatsIcon-label {
		@include text(4);
		line-height: 1.3;
	}
	.StatsIcon-secondary {
		@include text(3);
		line-height: 1.3;
	}
	.StatsIcon-status {
		@include text(3);
		line-height: 1.3;
		.Button {
			@include text(3);
		}
	}
}

/* --------------------------------
	Color mixins
-------------------------------- */

/* Color (default) */
@mixin StatsIcon--color {
	color: $color-text;
	border-color: $color-line;
	.StatsIcon-secondary {
		color: $color-text-muted;
	}
	.StatsIcon-status {
		color: $color-text-alert;
	}
}

/* Reverse color */
@mixin StatsIcon--colorReverse {
	color: $color-text-reverse;
	border-color: $color-line-reverse;
	.StatsIcon-secondary {
		color: $color-text-muted-reverse;
	}
	.StatsIcon-status {
		color: $color-text-alert-reverse;
	}
}

// Muted color
@mixin StatsIcon--colorMuted {
	color: $color-text-muted;
}

// Reverse muted color
@mixin StatsIcon--colorMutedReverse {
	color: $color-text-muted-reverse;
}

// Linked color
@mixin StatsIcon--colorLink {
	@include interactiveColor(color, $color-link, $color-link-hover);
	@include interactiveColor(border-color, subdueColor($color-link), $color-link-hover);
}

// Active color
@mixin StatsIcon--colorActive {
	@include interactiveColor(color, $color-link-special, $color-link-special-hover);
	@include interactiveColor(border-color, $color-link-special-hover, $color-link-special-hover);
}

// Reverse linked color
@mixin StatsIcon--colorLinkReverse {
	@include interactiveColor(color, $color-link-reverse, $color-link-hover-reverse);
	@include interactiveColor(border-color, subdueColor($color-link-reverse, true), $color-link-hover-reverse);
}

// Reverse active color
@mixin StatsIcon--colorActiveReverse {
	@include interactiveColor(color, $color-link-special-reverse, $color-link-special-hover-reverse);
	@include interactiveColor(border-color, $color-link-special-hover-reverse, $color-link-special-hover-reverse);
}

// Tab colors
@mixin StatsIcon--colorTab {
	@include interactiveColor(background-color, $brand-color-ocean-blue-2, $brand-color-ocean-blue-3);
	@include interactiveColor(border-color, transparent, subdueColor($color-link-hover));
}

// Selected tab colors
@mixin StatsIcon--colorTabSelected {
	@include interactiveColor(background-color, $brand-color-forest-green-3, $brand-color-forest-green-3);
	@include interactiveColor(border-color, subdueColor($color-link-special-hover), subdueColor($color-link-special-hover));
}

// Reverse tab colors
@mixin StatsIcon--colorTabReverse {
	@include interactiveColor(background-color, $brand-color-ocean-blue-dark-2, $brand-color-ocean-blue-dark-3);
	@include interactiveColor(border-color, transparent, subdueColor($color-link-hover-reverse, true));
}

// Reverse selected tab colors
@mixin StatsIcon--colorTabSelectedReverse {
	@include interactiveColor(background-color, $brand-color-forest-green-dark-3, $brand-color-forest-green-dark-3);
	@include interactiveColor(
		border-color,
		subdueColor($color-link-special-hover-reverse, true),
		subdueColor($color-link-special-hover-reverse, true)
	);
}

/* --------------------------------
	Layout mixins
-------------------------------- */

// Vertical line before stat
@mixin StatsIcon--line {
	border-width: $space-xxs;
	border-inline-start-style: solid;
	padding-inline-start: 0.35em;
}

// Tabbed stats with an inset box around them
@mixin StatsIcon--tab {
	padding: 0.25em 0.35em;
	border-radius: $radius-sm;
	background-color: $brand-color-sand-1;
	border-width: 1px;
	border-style: solid;
	border-color: transparent;
}

/* Vertical variant wraps and centers stats */
@mixin StatsIcon--vertical {
	text-align: start;
	.StatsIcon-stat {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.1em;
	}
}

@mixin StatsIcon--verticalCenter {
	text-align: center;
	.StatsIcon-stat {
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.1em;
	}
}

/* Inline makes labels appear inline with stat */
@mixin StatsIcon--inline {
	display: inline-flex;
	flex-direction: row;
	flex-wrap: wrap;
	align-items: baseline;
	gap: $space-rel-xs;
	row-gap: 0;
	.StatsIcon-stat {
		align-items: baseline;
		.Icon {
			align-self: center;
		}
	}
	.StatsIcon-label,
	.StatsIcon-secondary {
		align-items: baseline;
	}
	.StatsIcon-label {
		font-weight: bold;
		// @include text(4);
	}
}

// Label above places label above icon and count
@mixin StatsIcon--labelAbove {
	.StatsIcon-label {
		order: -1;
	}
}

/* ------------------------------------------------
---------------------------------------------------
	VARIANTS
---------------------------------------------------
------------------------------------------------ */

/* ----------------
	Size Variants
---------------- */

// Default sizing is responsive from sm to md
.StatsIcon {
	@include StatsIcon--sm;
	@include breakpoint(medium) {
		@include StatsIcon--md;
	}
	// These sizes override the default responsive behavior
	&.StatsIcon--sm {
		@include StatsIcon--sm;
		@include breakpoint(medium) {
			@include StatsIcon--sm;
		}
	}
	&.StatsIcon--md {
		@include StatsIcon--md;
		@include breakpoint(medium) {
			@include StatsIcon--md;
		}
	}
	&.StatsIcon--lg {
		@include StatsIcon--lg;
		@include breakpoint(medium) {
			@include StatsIcon--lg;
		}
	}
}

/* ----------------
	Color Variants
---------------- */

// Default colors
.StatsIcon {
	@include StatsIcon--color;
	&.StatsIcon--muted {
		@include StatsIcon--colorMuted;
	}
	// legacy class names
	&.StatsIcon--highlight,
	&.StatsIcon--selected {
		@include StatsIcon--colorActive;
	}
}
a.StatsIcon {
	@include StatsIcon--colorLink;
	&[aria-current='true'] {
		@include StatsIcon--colorActive;
	}
	// legacy class names
	&.StatsIcon--highlight,
	&.StatsIcon--selected {
		@include StatsIcon--colorActive;
	}
}

// Reverse colors
.StatsIcon {
	&.StatsIcon--reverse {
		@include StatsIcon--colorReverse;
		&.StatsIcon--muted {
			@include StatsIcon--colorMutedReverse;
		}
		// legacy class names
		&.StatsIcon--highlight,
		&.StatsIcon--selected {
			@include StatsIcon--colorActiveReverse;
		}
	}
}
a.StatsIcon {
	&.StatsIcon--reverse {
		@include StatsIcon--colorLinkReverse;
		&[aria-current='true'] {
			@include StatsIcon--colorActiveReverse;
		}
		// legacy class names
		&.StatsIcon--highlight,
		&.StatsIcon--selected {
			@include StatsIcon--colorActiveReverse;
		}
	}
}

/* ----------------
	Layout Variants
---------------- */

// Tabbed stats with an inset box around them
.StatsIcon {
	&.StatsIcon--tab {
		@include StatsIcon--tab;
	}
}
a.StatsIcon {
	&.StatsIcon--tab {
		@include StatsIcon--colorTab;
		&[aria-current='true'] {
			@include StatsIcon--colorTabSelected;
		}
	}
	&.StatsIcon--reverse.StatsIcon--tab {
		@include StatsIcon--colorTabReverse;
		&[aria-current='true'] {
			@include StatsIcon--colorTabSelectedReverse;
		}
	}
}

// Lined stats
.StatsIcon {
	&.StatsIcon--line {
		@include StatsIcon--line;
	}
}

// Vertical
.StatsIcon {
	&.StatsIcon--vertical {
		@include StatsIcon--verticalCenter;
	}
	/* Small vertical wraps and centers stats only on small screens */
	&.StatsIcon--sm-vertical {
		@include breakpoint(mediumsmall, down) {
			@include StatsIcon--verticalCenter;
		}
	}
	&.StatsIcon--line.StatsIcon--sm-vertical {
		@include breakpoint(mediumsmall, down) {
			@include StatsIcon--vertical;
		}
	}
}

/* Inline variant makes labels appear inline with stat */
.StatsIcon--inline {
	@include StatsIcon--inline;
}

// Label above places label above icon and count
.StatsIcon--labelAbove {
	.StatsIcon-label {
		order: -1;
	}
}

// --------------------------------
// Deprecated styles
// --------------------------------
a .StatsIcon {
	@include inset-md;
	color: $color-primary-3;
	background-color: $color-primary-0;
	border-radius: 3px;
}

a:active .StatsIcon {
	color: $color-white;
	background-color: $color-secondary-3;
}
</style>
